import React, { Component } from 'react';
import { Field, reduxForm, arrayRemove, change, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import Select from 'components/forms/Select';
import PropTypes from 'prop-types';
import Button from 'components/buttons/Button';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { resultsToOptions } from 'utils/misc';

import Error from 'components/forms/Error';
import RenderFormInput from 'components/forms/RenderFormInput';
import { getIndustries } from 'redux/actions/industryActions';
import { requiredValidator, emailValidator, charsOnlyValidator,
  handleBlur, requiredOptionValidator, requiredObjectValidator } from 'utils/validators';
import RenderReactTags from '../../../components/forms/RenderReactTags';


const styles = theme => ({
  redAsterisk: {
    color: theme.palette.accent3.dark,
    fontSize: '15px',
  },
});

class AccountForm extends Component {
  static propTypes = {
    getIndustries: PropTypes.func.isRequired,
    changeArray: PropTypes.func.isRequired,
    products: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })).isRequired,
    removeArray: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    toggleModal: PropTypes.func,
    industries: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.number,
      })),
    }).isRequired,
    organizations: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })).isRequired,
    submitting: PropTypes.bool,
    pristine: PropTypes.bool,
    classes: PropTypes.shape({}).isRequired,
  };

  static defaultProps = {
    toggleModal: null,
    submitting: false,
    pristine: true,
  };

  constructor(props) {
    super(props);
    this.state = {};
    this.handleAddition = this.handleAddition.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  componentDidMount() {
    // loading options for industries
    this.props.getIndustries();
  }

  handleAddition(product) {
    const values = [...this.props.products, product];
    this.props.changeArray('accountCreateForm', 'selectedProducts', values);
  }

  handleDelete(i) {
    this.props.removeArray('accountCreateForm', 'selectedProducts', i);
  }

  render() {
    const {
      handleSubmit, submitting, pristine, classes,
    } = this.props;
    const isIndustryLoading = this.props.industries.loading;

    return (
      <form className="instance-form" onSubmit={handleSubmit}>
        {(isIndustryLoading) && <div className="loader"><LoadingIcon /></div>}
        <div className="instance-form__body">

          {/* eslint-disable jsx-a11y/label-has-for */}
          <label>Account name <span className={classes.redAsterisk}>*</span></label>
          <Field
            maxLength="255"
            name="accountName"
            component={RenderFormInput}
            type="text"
            onBlur={handleBlur}
            validate={[requiredValidator, charsOnlyValidator]}
          />

          {/* eslint-disable jsx-a11y/label-has-for */}
          <label className="mt-4">Primary email</label>
          <Field
            maxLength="255"
            name="email"
            component={RenderFormInput}
            type="email"
            onBlur={handleBlur}
            validate={[emailValidator]}
          />

          {/* eslint-disable jsx-a11y/label-has-for */}
          <label className="mt-4">Products <span className={classes.redAsterisk}>*</span></label>
          <Field
            name="selectedProducts"
            onBlur={handleBlur}
            validate={[requiredObjectValidator]}
            component={propsCus => (
              <React.Fragment>
                <RenderReactTags
                  handleAddition={this.handleAddition}
                  tags={propsCus.input.value}
                  handleDelete={this.handleDelete}
                  placeholder="Type a product name and press enter"
                  labelField="name"
                  handleInputBlur={() => propsCus.input.onBlur(propsCus.input.value)}
                />
                {propsCus.meta.touched &&
                propsCus.meta.error && <Error error={propsCus.meta.error} />}
              </React.Fragment>
            )}
          />

          {/* eslint-disable jsx-a11y/label-has-for */}
          <label className="mt-4">Industry tags</label>
          <Field
            name="selectedIndustryTags"
            onBlur={handleBlur}
            component={props => (
              <Select
                isMulti
                value={props.input.value}
                onChange={props.input.onChange}
                onBlur={() => props.input.onBlur(props.input.value)}
                options={this.props.industries.options || []}
              />
            )}
          />

          {this.props.organizations.length > 1 &&
            <>
              <label className="mt-4">Organization <span className={classes.redAsterisk}>*</span></label>
              <Field
                name="selectedOrganization"
                onBlur={handleBlur}
                validate={[requiredOptionValidator]}
                component={propsCus => (
                    <>
                      <Select
                        value={propsCus.input.value}
                        onChange={propsCus.input.onChange}
                        onBlur={() => propsCus.input.onBlur(propsCus.input.value)}
                        options={resultsToOptions(this.props.organizations, 'name')}
                      />
                      {propsCus.meta.touched &&
                      propsCus.meta.error && <Error error={propsCus.meta.error} />}
                    </>
                )}
              />
            </>
          }
        </div>
        <div className="instance-form__footer">
          {this.props.toggleModal && (
            <Button type="button" color="secondary" variant="text" onClick={this.props.toggleModal}>Cancel</Button>
          )}
          <Button color="primary" variant="contained" className="ml-2" disabled={pristine || submitting} type="submit">
            Add
          </Button>
        </div>
      </form>
    );
  }
}

const selector = formValueSelector('accountCreateForm');

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      products: selector(state, 'selectedProducts'),
      industries: state.industries,
      organizations: state.user.organizations,
      initialValues: {
        acceptsSponsorships: false,
        email: '',
        selectedProducts: [],
        selectedIndustryTags: [],
        selectedOrganization: {},
      },
    }),
    {
      getIndustries, removeArray: arrayRemove, changeArray: change,
    },
  ),
  reduxForm({
    form: 'accountCreateForm',
  }),
)(AccountForm);
